import React, { useContext } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { RichTextInput } from "../../input";
import GrigoraCSSRenderer from "./../css-render";
import { FAQSettings } from "./settings";
import { CMSFAQSettings } from "./cms-settings";
import icon from "./icon";
import classnames from "classnames";
import { getDynamicCSS, getSupportProps } from "../../block-supports";
import CMSIcon from "./cms-icon";
import {
  getCaretPosition,
  restoreCaretPosition,
  waitForElement,
} from "../../dom";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { IconsRenderer } from "../../../assets/icon-pack";
import { IS_MAC } from "../../utils";
import { updateTitleWrapper } from "./utils";

export const FAQ = (props) => {
  const { editorDocument, screen } = useContext(GrigoraDashboardContext) ?? {};

  const {
    id,
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode((state) => ({
    id: state.id,
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));

  const {
    actions,
    actions: { selectNode },
  } = useEditor();

  const {
    question = [""],
    answer = [""],
    listStyle = "ul",
    titleWrapper = "h3",
  } = props;

  var blockProps = getSupportProps({
    id,
    ...props,
  });

  const wrapperClass = classnames(
    {
      "g-faq": true,
      [`faq-${id}`]: id,
    },
    blockProps.className,
  );

  const addNewFAQ = () => {
    var tempQuestion = [...question];
    var tempAnswer = [...answer];

    tempQuestion.push("");
    tempAnswer.push("");

    var questionIndex = tempQuestion.length;

    setProp((props) => {
      props.question = tempQuestion;
      props.answer = tempAnswer;
    });

    // Focus on the new FAQ
    waitForElement(
      editorDocument,
      `.faq-${id} .faq-${questionIndex} .grigora-richtext-input__field`,
    ).then((element) => {
      element.focus();
    });
  };

  const deleteFAQ = (i) => {
    var tempQuestion = [...question];
    var tempAnswer = [...answer];

    if (i > -1) {
      tempQuestion.splice(i, 1);
      tempAnswer.splice(i, 1);
    }
    setProp((props) => {
      props.question = tempQuestion;
      props.answer = tempAnswer;
    });
  };
  const ListStyle = listStyle;

  const handleOnKeydown = (e) => {
    const ctrlKey = IS_MAC ? e.metaKey : e.ctrlKey;

    if (ctrlKey) {
      // ctrl + alt
      if (e.altKey) {
        switch (e.keyCode) {
          // for heading 1 / H1
          case 49: {
            if (titleWrapper !== "h1") {
              updateTitleWrapper("h1", editorDocument, id, actions);
            } else {
              updateTitleWrapper("p", editorDocument, id, actions);
            }
            e.preventDefault();
            break;
          }
          // for heading 2 / H2
          case 50: {
            if (titleWrapper !== "h2") {
              updateTitleWrapper("h2", editorDocument, id, actions);
            } else {
              updateTitleWrapper("p", editorDocument, id, actions);
            }
            e.preventDefault();
            break;
          }
          // for heading 3 / H3
          case 51: {
            if (titleWrapper !== "h3") {
              updateTitleWrapper("h3", editorDocument, id, actions);
            } else {
              updateTitleWrapper("p", editorDocument, id, actions);
            }
            e.preventDefault();
            break;
          }
          // for heading 4 / H4
          case 52: {
            if (titleWrapper !== "h4") {
              updateTitleWrapper("h4", editorDocument, id, actions);
            } else {
              updateTitleWrapper("p", editorDocument, id, actions);
            }
            e.preventDefault();
            break;
          }
          // for heading 5 / H5
          case 53: {
            if (titleWrapper !== "h5") {
              updateTitleWrapper("h5", editorDocument, id, actions);
            } else {
              updateTitleWrapper("p", editorDocument, id, actions);
            }
            e.preventDefault();
            break;
          }
          // for heading 6 / H6
          case 54: {
            if (titleWrapper !== "h6") {
              updateTitleWrapper("h6", editorDocument, id, actions);
            } else {
              updateTitleWrapper("p", editorDocument, id, actions);
            }
            e.preventDefault();
            break;
          }
          default: {
            break;
          }
        }
      }
    }
  };

  return (
    <ListStyle
      {...blockProps}
      {...(screen === "cms-post-editor"
        ? { ref: (ref) => connect(ref) }
        : { ref: (ref) => connect(drag(ref)) })}
      className={wrapperClass}
      onFocus={() => {
        selectNode(id);
      }}
      onKeyDown={handleOnKeydown}
      data-grigora-block="true"
      data-grigora-block-id={id}
    >
      <style>{`${getDynamicCSS(id, props)}`}</style>
      <style>{`${GrigoraCSSRenderer.blockCSS__FAQ(id, props)}`}</style>
      {question.map((currentQuestion, i) => {
        return (
          <li
            key={`faq-item-${id}-${i + 1}`}
            className={`faq-container faq-${i + 1}`}
          >
            <div className={`faq-question-container`}>
              <RichTextInput
                placeholder={"Question..."}
                value={currentQuestion}
                onChange={(e) => {
                  setProp((props) => {
                    var tempFAQQuestion = [
                      ...(props.question ? props.question : []),
                    ];
                    tempFAQQuestion[i] = e;
                    props.question = tempFAQQuestion;
                    return props;
                  }, 500);
                }}
                tagName={titleWrapper}
                onEnter={() => {}}
                className="qu"
              />
              {question.length > 1 && (
                <div className="faq-action-container">
                  <IconsRenderer
                    name="Delete"
                    onClick={() => deleteFAQ(i)}
                    className="trash-icon"
                  />
                </div>
              )}
            </div>
            <div className="faq-answer-container">
              <RichTextInput
                placeholder={"Answer..."}
                value={answer[i]}
                onChange={(e) => {
                  setProp((props) => {
                    var tempFAQAnswer = [...(props.answer ? props.answer : [])];
                    tempFAQAnswer[i] = e;
                    props.answer = tempFAQAnswer;
                    return props;
                  }, 500);
                }}
                tagName={"p"}
              />
            </div>
          </li>
        );
      })}
      <button
        onClick={addNewFAQ}
        contentEditable={false}
        className="faq-new-button select-none"
      >
        Add New FAQ
      </button>
    </ListStyle>
  );
};
// To Avoid Mangling in Production
FAQ.displayName = "FAQ";

FAQ.craft = {
  name: "FAQ",
  props: {},
  related: {
    settings: FAQSettings,
    CMSsettings: CMSFAQSettings,
    supports: [
      "grigora/entrance-animations",
      "grigora/css-transform-animations",
      "grigora/mouse-movement",
      "grigora/visibility",
      "grigora/position",
      "grigora/sticky",
      "grigora/filter",
      "grigora/custom-class",
      "grigora/custom-id",
    ],
    icon: icon,
    CMSIcon: CMSIcon,
    keywords: [
      "faq",
      "frequently asked questions",
      "asked",
      "questions",
      "answers",
    ],
  },
  custom: {
    name: "FAQ",
    slug: "grigora/faq",
  },
};
